import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, X, Briefcase, GraduationCap, CheckCircle2, ArrowRight } from "lucide-react";
import { ProfileCompletion } from "@/components/ProfileCompletion";

export default function Welcome() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", location: "", summary: "" });
  const [work, setWork] = useState([]);
  const [edu, setEdu] = useState([]);
  const [skills, setSkills] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchProfile = async () => {
    const { data } = await api.get("/profile");
    const p = data.profile || {};
    setForm({ full_name: p.full_name || "", email: p.email || "", phone: p.phone || "", location: p.location || "", summary: p.summary || "" });
    setWork(data.work_experiences || []);
    setEdu(data.educations || []);
    setSkills(data.skills || []);
  };

  useEffect(() => {
    (async () => {
      const leadId = localStorage.getItem("welcome_lead") || undefined;
      if (leadId) {
        localStorage.removeItem("welcome_lead");
        setImporting(true);
        try { await api.post("/free-analysis/import-profile", { lead_id: leadId }); } catch (e) { console.error("Profile import failed (non-blocking):", e); }
        setImporting(false);
      }
      try {
        await fetchProfile();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load your profile.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const importFromCv = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/free-analysis/import-profile", {});
      if (data.imported) {
        await fetchProfile();
        toast.success(data.filled?.length ? "Imported details from your CV." : "Your profile is already up to date.");
      } else {
        toast.info("We couldn't find a CV linked to your account to import from.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const removeItem = async (kind, id) => {
    const path = { work: "work-experiences", edu: "educations", skills: "skills" }[kind];
    const setter = { work: setWork, edu: setEdu, skills: setSkills }[kind];
    try {
      await api.delete(`/${path}/${id}`);
      setter((list) => list.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Couldn't remove that item.");
    }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      toast.success("Profile confirmed — you're all set!");
      navigate("/profile");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save your profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="py-24 text-center" data-testid="welcome-loading">
      <Loader2 className="h-8 w-8 mx-auto animate-spin text-accent" />
      {importing && <p className="text-sm text-muted-foreground mt-3">Importing details from your CV…</p>}
    </div>
  );

  const hasImport = work.length || edu.length || skills.length || form.summary;

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="welcome-page">
      <div className="text-center">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-accent bg-accent/10 px-3 py-1 rounded-full">
          <Sparkles className="h-3.5 w-3.5" /> Welcome to Jobsmith
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-4">Review your profile</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {hasImport
            ? "We pre-filled this from the CV you uploaded. Review it, fix anything, remove what's wrong, then confirm."
            : "Add your details below to complete your profile."}
        </p>
        <Button variant="outline" size="sm" onClick={importFromCv} disabled={importing} className="mt-4" data-testid="welcome-import-btn">
          {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Import from my CV
        </Button>
      </div>

      <ProfileCompletion profile={form} work={work} edu={edu} skills={skills} />

      <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Your details</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Full name</Label><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="welcome-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="welcome-email" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Add your phone" data-testid="welcome-phone" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Add your location" data-testid="welcome-location" /></div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Professional summary</Label><Textarea rows={4} value={form.summary} onChange={(e) => set("summary", e.target.value)} placeholder="A short summary of your experience" data-testid="welcome-summary" /></div>
      </div>

      {work.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-3" data-testid="welcome-work">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"><Briefcase className="h-4 w-4" /> Work experience ({work.length})</p>
          {work.map((w) => (
            <div key={w.id} className="flex items-start justify-between gap-3 border border-zinc-100 rounded-lg p-3">
              <div>
                <p className="font-medium text-sm">{w.role}{w.company ? ` · ${w.company}` : ""}</p>
                <p className="text-xs text-muted-foreground">{[w.start_date, w.end_date].filter(Boolean).join(" – ")}</p>
                {w.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{w.description}</p>}
              </div>
              <button onClick={() => removeItem("work", w.id)} className="text-zinc-400 hover:text-red-600 transition-colors shrink-0" data-testid={`welcome-remove-work-${w.id}`}><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      {edu.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-3" data-testid="welcome-edu">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"><GraduationCap className="h-4 w-4" /> Education ({edu.length})</p>
          {edu.map((e) => (
            <div key={e.id} className="flex items-start justify-between gap-3 border border-zinc-100 rounded-lg p-3">
              <div>
                <p className="font-medium text-sm">{e.degree}{e.institution ? ` · ${e.institution}` : ""}</p>
                <p className="text-xs text-muted-foreground">{[e.start_date, e.end_date].filter(Boolean).join(" – ")}</p>
              </div>
              <button onClick={() => removeItem("edu", e.id)} className="text-zinc-400 hover:text-red-600 transition-colors shrink-0" data-testid={`welcome-remove-edu-${e.id}`}><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      {skills.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-3" data-testid="welcome-skills">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Skills ({skills.length})</p>
          <div className="flex flex-wrap gap-2">
            {skills.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5 bg-zinc-100 text-sm rounded-full pl-3 pr-2 py-1" data-testid={`welcome-skill-${s.id}`}>
                {s.name}
                <button onClick={() => removeItem("skills", s.id)} className="text-zinc-400 hover:text-red-600 transition-colors"><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pb-4">
        <button onClick={() => navigate("/profile")} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="welcome-skip">Skip for now</button>
        <Button onClick={confirm} disabled={saving} className="h-11 px-6 active:scale-[0.98] transition-transform" data-testid="welcome-confirm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Confirm &amp; continue <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
}
